import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { PrejudiceType } from '@norma/shared/types/prejudice.type';
import { Procedure } from '@norma/shared/types/procedure.type';
import { Victime } from '@norma/shared/types/victime.type';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { useDisplayedOrganization } from 'src/hooks/store/organization.hooks';
import { fetchExtractionByVictimeId } from 'src/slices/extraction';
import { deletePrejudiceByTypes } from 'src/slices/prejudice';
import { baremeSelectors, monetaryErosionSelectors } from 'src/store/selectors';

interface ReparseExtractionDialogProps {
  open: boolean;
  onClose: () => void;
  procedure: Procedure;
  victime: Victime;
}

const ReparseExtractionDialog: React.FC<ReparseExtractionDialogProps> = ({
  open,
  onClose,
  procedure,
  victime,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { organization } = useDisplayedOrganization();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { baremes, monetaryErosions } = useAppSelector((state) => ({
    baremes: baremeSelectors.selectAll(state),
    monetaryErosions: monetaryErosionSelectors.selectAll(state),
  }));

  const handleReparse = async () => {
    if (!procedure || !victime || !organization) return;

    setIsLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Fetch extraction data by victim ID
      const extractionResult = await dispatch(
        fetchExtractionByVictimeId({
          organizationId: organization._id,
          victimeId: victime._id,
        }),
      ).unwrap();

      if (!extractionResult.extractionData) {
        throw new Error('No extraction data found for this victim');
      }

      // Delete existing prejudices
      const allPrejudiceTypes: PrejudiceType[] = [
        'DEFICIT_FONCTIONNEL_TEMPORAIRE_PARTIEL',
        'DEFICIT_FONCTIONNEL_TEMPORAIRE_TOTAL',
        'ASSISTANCE_PAR_TIERCE_PERSONNE_ACTUELLE',
        'ASSISTANCE_PAR_TIERCE_PERSONNE_FUTURE',
        'PREJUDICE_SCOLAIRE_UNIVERSITAIRE_FORMATION',
        'INCIDENCE_PROFESSIONNELLE',
        'PREJUDICE_AGREMENT',
        'PREJUDICE_SEXUEL',
        'DEFICIT_FONCTIONNEL_PERMANENT',
        'PREJUDICE_ESTHETIQUE_PERMANENT',
        'SOUFFRANCES_ENDUREES',
        'PREJUDICE_ESTHETIQUE_TEMPORAIRE',
        'PERTES_GAINS_PROFESSIONNELS_FUTURS',
        'PERTES_GAINS_PROFESSIONNELS_ACTUELS',
        'DEPENSE_SANTE_ACTUELLES',
        'DEPENSE_SANTE_FUTURES',
        'FRAIS_LOGEMENT_ADAPTE',
        'FRAIS_VEHICULE_ADAPTE',
        'PREJUDICE_ETABLISSEMENT',
        'PREJUDICE_ANXIETE',
      ];

      await dispatch(
        deletePrejudiceByTypes({
          procedureId: procedure._id,
          types: allPrejudiceTypes,
        }),
      ).unwrap();

      // Create new prejudices
      // await dispatch(
      //   createMultiplePrejudices({
      //     procedureId: procedure._id,
      //     dto: parsedPrejudices,
      //   }),
      // ).unwrap();

      setProgress(100);
      clearInterval(progressInterval);

      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
        setIsLoading(false);
        setError(null);
        setProgress(0);
      }, 1000);
    } catch (e) {
      console.error(e);
      setError(e instanceof Error ? e.message : 'An error occurred');
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('pages.Procedure.reparseExtraction.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t('pages.Procedure.reparseExtraction.description')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {isLoading && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mb: 2 }}
              >
                {t('pages.Procedure.reparseExtraction.processing')}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 1 }}
              >
                {Math.round(progress)}%
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleReparse}
          disabled={isLoading}
          variant="contained"
          color="primary"
        >
          {t('pages.Procedure.reparseExtraction.buttonLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReparseExtractionDialog;
